import { useState } from "react";
import { Loader2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useMeetings } from "@/providers/MeetingsProvider";
import { useToast } from "@/hooks/use-toast";
import type { MeetingType, MeetingRequestTemplate } from "@shared/types/MeetingDto";
import type { CreateMeetingsRequestPayload } from "@shared/types/payloads/CreateMeetingsRequestPayload";

const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  INTRO_DISCOVERY: "Intro / Discovery",
  STRATEGIC_CONSULTATION: "Strategic Consultation",
  TECHNICAL_SCOPING: "Technical Scoping",
  SERVICE_ORDER_REFINEMENT: "Service Order Refinement",
  MILESTONE_DEMO: "Milestone Demo",
  OFFICE_HOURS: "Office Hours",
};

interface CreateMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateMeetingDialog({
  open,
  onOpenChange,
}: CreateMeetingDialogProps) {
  const { templates, createMeeting, isActionLoading } = useMeetings();
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] =
    useState<MeetingRequestTemplate | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<MeetingType>("INTRO_DISCOVERY");
  const [duration, setDuration] = useState("30");
  const [createdMagicLink, setCreatedMagicLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleTemplateSelect = (template: MeetingRequestTemplate) => {
    setSelectedTemplate(template);
    setTitle(template.label);
    setType(template.type);
    setDuration(String(template.defaultDuration));
  };

  const handleCreate = async () => {
    if (!title.trim()) return;

    try {
      const payload: CreateMeetingsRequestPayload = {
        title: title.trim(),
        type,
        durationMinutes: parseInt(duration, 10),
      };

      const meeting = await createMeeting(payload);

      if (meeting?.magicLink) {
        setCreatedMagicLink(meeting.magicLink);
      } else {
        toast({
          title: "Meeting request created",
          description: "Your meeting has been created successfully.",
          variant: "success",
        });
        resetAndClose();
      }
    } catch (err) {
      toast({
        title: "Failed to create meeting",
        description:
          err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = async () => {
    if (!createdMagicLink) return;
    await navigator.clipboard.writeText(createdMagicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAndClose = () => {
    setSelectedTemplate(null);
    setTitle("");
    setType("INTRO_DISCOVERY");
    setDuration("30");
    setCreatedMagicLink(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent
        className="sm:max-w-lg"
        aria-label="Create meeting dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {createdMagicLink ? "Meeting created" : "Request a meeting"}
          </DialogTitle>
        </DialogHeader>

        {createdMagicLink ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Share this link to schedule the meeting:
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={createdMagicLink}
                readOnly
                className="flex-1 text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyLink}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={resetAndClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Quick templates</Label>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <Badge
                      key={t.id}
                      variant={
                        selectedTemplate?.id === t.id ? "default" : "outline"
                      }
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleTemplateSelect(t)}
                    >
                      {t.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="meeting-title">Title</Label>
              <Input
                id="meeting-title"
                placeholder="Meeting title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as MeetingType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MEETING_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meeting-duration">Duration (minutes)</Label>
              <Input
                id="meeting-duration"
                type="number"
                min="15"
                step="15"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!title.trim() || isActionLoading}
              >
                {isActionLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create meeting"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
